/**
 * Definisce l'interfaccia pubblica del modulo di accesso a LocalStorage.
 */
interface PrefixedStorageModule {
    /**
     * Imposta un valore con la chiave prefissata.
     * @param key - La chiave "logica" (senza prefisso).
     * @param value - Il valore da salvare (può essere di qualsiasi tipo, verrà serializzato).
     */
    setItem: (key: string, value: any) => void;

    /**
     * Ottiene un valore usando la chiave prefissata.
     * @param key - La chiave "logica" (senza prefisso).
     * @returns Il valore deserializzato o null se la chiave non è presente.
     */
    getItem: (key: string) => string | null;

    /**
     * Rimuove un elemento usando la chiave prefissata.
     * @param key - La chiave "logica" (senza prefisso).
     */
    removeItem: (key: string) => void;

    getItemParsed: (key: string, fallbackValue?: any) => any;
}

/**
 * Crea e restituisce un modulo per accedere a localStorage con un prefisso specifico.
 * Ogni chiave set/get viene automaticamente prefissata/deprefissata.
 *
 * @param prefix - Il prefisso da usare per tutte le chiavi (es: 'USER_', 'CONFIG_').
 * @returns Un oggetto che implementa PrefixedStorageModule.
 */
export function createPrefixedStorage(prefix: string): PrefixedStorageModule {
    // Assicurati che localStorage sia disponibile; altrimenti usa un fallback (es. un oggetto vuoto)
    const storage: Storage = window.localStorage || {
        // Implementazione minima di un oggetto Storage fittizio per evitare errori di runtime
        length: 0,
        clear: () => {},
        getItem: () => null,
        setItem: () => {},
        removeItem: () => {},
        key: () => null,
    };

    // Funzione helper per ottenere la chiave effettiva in localStorage
    const getStorageKey = (key: string): string => `${prefix}${key}`;

    return {
        setItem: (key, value) => {
            const storageKey = getStorageKey(key);
            try {
                // La tipizzazione aiuta a ricordare che JSON.stringify è necessario
                storage.setItem(storageKey, value);
            } catch (e) {
                console.error(
                    `[Storage Error] Errore nel salvare la chiave: ${storageKey}`,
                    e,
                );
            }
        },

        getItem: (key): string | null => {
            const storageKey = getStorageKey(key);
            return storage.getItem(storageKey);
        },

        removeItem: (key) => {
            const storageKey = getStorageKey(key);
            storage.removeItem(storageKey);
        },

        getItemParsed: (key: string, fallbackValue: any = null) => {
            const storageKey = getStorageKey(key);
            const item = storage.getItem(storageKey);
            if (item === null) return fallbackValue;
            try {
                return JSON.parse(item);
            } catch (e) {
                console.error(e);
                throw new Error(
                    `[Storage Error] Errore nel JSON.parse della chiave: ${storageKey}, controlla dove viene usato getItemParsed()`,
                );
            }
        },
    };
}

export function getLocalStorage() {
    const tourAttr = document.documentElement.getAttribute('data-tour');
    let isInTour = tourAttr === 'on';
    if (tourAttr === null) {
        const tourParam = new URLSearchParams(window.location.search).get(
            'tour',
        );
        if (tourParam === 'on') {
            document.documentElement.setAttribute('data-tour', 'on');
            isInTour = true;
        } else {
            document.documentElement.setAttribute('data-tour', 'off');
            isInTour = false;
        }
    }
    // console.log({isInTour, tourAttr, tour: new URLSearchParams(window.location.search).get('tour') })

    return createPrefixedStorage(isInTour ? 'tour_' : '');
}
